import { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Panel } from '@/components/ui';
import type { PedidoFactura } from '@/types';

interface Props {
  folio: string;
  factura: PedidoFactura;
  onActualizar: (factura: PedidoFactura) => void;
}

export function PedidoFacturaPanel({ folio, factura, onActualizar }: Props) {
  const [uso, setUso] = useState(factura.uso_cfdi || 'G03');
  const [timbrando, setTimbrando] = useState(false);
  const [descargando, setDescargando] = useState<'xml' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const timbrar = async () => {
    setTimbrando(true);
    setError(null);
    try {
      const actualizada = await api.authPedidoFacturar(folio, uso);
      onActualizar(actualizada);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo timbrar la factura');
    } finally {
      setTimbrando(false);
    }
  };

  const bajar = async (tipo: 'xml' | 'pdf') => {
    setDescargando(tipo);
    setError(null);
    try {
      await api.authPedidoFacturaArchivo(folio, tipo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo descargar');
    } finally {
      setDescargando(null);
    }
  };

  const catalogo = factura.catalogo_uso_cfdi ?? [];

  return (
    <Panel title="Factura CFDI" icon={<FileText className="h-4 w-4 text-brand-600" />}>
      {factura.estatus === 'facturada' && (
        <div className="space-y-4">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Factura timbrada. Ya puede descargar XML y PDF.
          </p>
          <dl className="space-y-2 text-sm">
            {(factura.serie || factura.folio) && (
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">Serie / folio</dt>
                <dd className="font-mono">
                  {factura.serie}
                  {factura.folio}
                </dd>
              </div>
            )}
            {factura.uuid && (
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">UUID</dt>
                <dd className="break-all text-right font-mono text-xs">{factura.uuid}</dd>
              </div>
            )}
            {factura.rfc && (
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">RFC</dt>
                <dd className="font-mono">{factura.rfc}</dd>
              </div>
            )}
            {factura.razon_social && (
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">Razón social</dt>
                <dd className="text-right">{factura.razon_social}</dd>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={descargando === 'pdf' || !factura.uuid}
              onClick={() => bajar('pdf')}
            >
              {descargando === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              PDF
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={descargando === 'xml' || !factura.tiene_xml}
              onClick={() => bajar('xml')}
            >
              {descargando === 'xml' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              XML
            </button>
          </div>
        </div>
      )}

      {factura.estatus === 'pendiente' && (
        <div className="space-y-4">
          <p className="text-sm text-surface-700">
            Este pedido está listo para facturar, igual que en Facturas V2. RFC y razón social salen de su
            cuenta Alarcom (no se editan aquí).
          </p>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-surface-500">RFC</dt>
              <dd className="font-mono">{factura.rfc || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-surface-500">Razón social</dt>
              <dd className="text-right">{factura.razon_social || '—'}</dd>
            </div>
          </dl>
          {catalogo.length > 0 && (
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-surface-600">Uso de CFDI</span>
              <select className="input-field" value={uso} onChange={(e) => setUso(e.target.value)}>
                {catalogo.map((op) => (
                  <option key={op.codigo} value={op.codigo}>
                    {op.etiqueta}
                  </option>
                ))}
              </select>
            </label>
          )}
          {factura.puede_timbrar ? (
            <button type="button" className="btn-primary w-full" disabled={timbrando} onClick={timbrar}>
              {timbrando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {timbrando ? 'Timbrando…' : 'Facturar ahora'}
            </button>
          ) : (
            <p className="text-sm text-amber-800">{factura.motivo}</p>
          )}
        </div>
      )}

      {factura.estatus === 'espera_pago' && (
        <p className="text-sm text-surface-600">{factura.motivo}</p>
      )}
      {factura.estatus === 'faltan_datos' && (
        <p className="text-sm text-surface-700">
          {factura.motivo}{' '}
          <a className="font-medium text-brand-700 underline" href="mailto:ventas@alarcom.mx">
            ventas@alarcom.mx
          </a>
        </p>
      )}
      {factura.estatus === 'no_solicitada' && (
        <p className="text-sm text-surface-600">{factura.motivo}</p>
      )}
      {factura.estatus === 'no_disponible' && (
        <p className="text-sm text-surface-600">{factura.motivo ?? 'Factura no disponible para este pedido.'}</p>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </Panel>
  );
}
